import { observable, action, computed, reaction, makeObservable } from 'mobx';
import { isSome } from 'core/utils';
import { getAppRouterState, getLoginRoute } from 'core/utils';
import { RouterState } from 'mobx-state-router';
import { PermissionService } from 'core/services';
import _ from 'lodash';
import { CoreUserInfoStore } from 'modules/core-user-info/stores';

class AuthStore {
	rootStore;

	@observable token = undefined;
	@observable user = null;
	@observable impersonatedUser = null;
	// Where should we redirect after sign in
	@observable signInRedirect = null;
	hasPiiAccess = false;
	@computed get isAuthenticated() {
		return isTokenValid(this.token);
	}

	constructor(rootStore) {
		makeObservable(this);
		this.rootStore = rootStore;

		// sync local token with baasic app token
		reaction(
			() => this.token,
			() => {
				if (!this.isAuthenticated) {
					this.resetPermissions();
					this.setUser(null);

					const currentRoute = this.rootStore.routerStore.routerState;
					if (currentRoute.isPrivate) {
						const appId = currentRoute.params && currentRoute.params.appId ? currentRoute.params.appId : undefined;
						this.rootStore.routerStore.goTo(getLoginRoute(appId));
					}
				}
			},
			{ equals: (from, to) => from === to }
		);
		this.coreUserInfoStore = new CoreUserInfoStore(rootStore);
		this.initAuthEvents();
	}

	@action.bound
	checkPiiAccess = async userId => {
		await this.coreUserInfoStore.findCoreUserInfo({ userId: userId });
		if (this.coreUserInfoStore.data.length > 0) {
			const userInfo = this.coreUserInfoStore.data[0];
			if (userInfo.piiAccess) {
				this.hasPiiAccess = true;
			} else {
				this.hasPiiAccess = false;
			}
		} else {
			this.hasPiiAccess = false;
		}
	};

	getSignInRedirect() {
		if (this.signInRedirect) {
			return this.signInRedirect;
		}

		return getAppRouterState('master.app.main.user.list');
	}

	@action updateAccessToken() {
		const { app } = this.rootStore;
		this.token = app && app.baasic ? app.baasic.getAccessToken() || null : null;
	}

	@action setUser = async user => {
		const app = this.rootStore.app.baasic;
		if (user) {
			app.setUser(user);
			this.user = user;
			await this.checkPiiAccess(this.user.id);
			this.rootStore.viewStore.countUnlinkedOrderLines();
		} else {
			this.resetPermissions();
			app.setUser(null);
			this.user = null;
		}
	};

	getImpersonatedUser() {
		return JSON.parse(localStorage.getItem(storageKey));
	}

	@action setImpersonatedUser(user) {
		if (user === undefined || user === null) {
			localStorage.removeItem(storageKey);
		} else {
			localStorage.setItem(storageKey, JSON.stringify(user));
		}

		this.impersonatedUser = user;
	}

	@action updateUser(user) {
		const currentUser = this.user ? { ...this.user, ...user } : user;
		this.setUser(currentUser);
	}

	@action setSignInRedirect(state) {
		this.signInRedirect = state;
	}

	resetSignInRedirect() {
		this.setSignInRedirect(null);
	}

	async initialize() {
		this.updateAccessToken();

		return Promise.resolve();
	}

	resetPermissions() {
		this.rootStore.app.baasic.membershipModule.permissions.resetPermissions();
	}

	@action.bound hasPermission(authorization = '') {
		const permissionService = new PermissionService(this.rootStore.app.baasic);
		var authorized = true;
		if (authorization) {
			if (typeof authorization === 'function') {
				return authorization();
			}

			var requestedAuthorization;
			if (_.isArray(authorization)) {
				requestedAuthorization = authorization;
			} else if (typeof authorization === 'string') {
				requestedAuthorization = [authorization];
			}

			var l = requestedAuthorization.length;
			var i = 0;
			while (i < l) {
				authorized = permissionService.hasPermission(requestedAuthorization[i++]);
				if (authorized) {
					break;
				}
			}
		} else {
			authorized = permissionService.hasPermission();
		}

		return authorized;
	}

	initAuthEvents() {
		document.addEventListener('tokenExpired', () => {
			this.updateAccessToken();
		});

		document.addEventListener('tokenUpdated', async () => {
			this.updateAccessToken();
		});
	}
}

function isTokenValid(token) {
	return (
		isSome(token) &&
		(token.expireTime === undefined || token.expireTime === null || token.expireTime - new Date().getTime() > 0)
	);
}

export default AuthStore;



// WEBPACK FOOTER //
// ./src/core/stores/AuthStore.js